// young-age-data.js - evaluation matrices + certificate content for the youngest
// bands (U7-U8, U9-U10). Self-contained and BILINGUAL (en/nl) because this is
// new content, decoupled from the legacy EVAL_DATA + NL-overlay plumbing.
//
// Design (see memory young-age-matrices-and-certificate):
//  - Star skills are TECHNICAL skills rated on a MILESTONE LADDER: 5 observable
//    can-do rungs. The trainer taps the highest rung the child reliably shows;
//    that 1-5 IS a normal rating (domain Technical, skill = `key`), so the
//    Dragons Score keeps working. The child sees the rung count as stars.
//  - "Hockey for life" is NOT scored. For each value the coach picks the ONE
//    positive line that best fits the child (options[]), or leaves it and the
//    `default` line is used. All options are positive - it is a celebration.
//
// IMPORTANT: every young team (U7..U12) currently shares age_group "U11-U12";
// the real age lives in the TEAM ID (U7G1, U9B2). So band comes from the id.

// ── Band from a team id ───────────────────────────────────────────────────────
export const YOUNG_BANDS = ['U7-U8', 'U9-U10'];

/** "U9G2" -> 9 ; null if it does not start with U<number>. */
export function ageFromTeamId(id) {
  const m = /^U(\d+)/i.exec(String(id || ''));
  return m ? parseInt(m[1], 10) : null;
}

/** Canonical band for a team id. Young bands use clean hyphen keys. */
export function bandForTeamId(id) {
  const a = ageFromTeamId(id);
  if (a == null) return null;
  if (a <= 8)  return 'U7-U8';
  if (a <= 9)  return 'U9-U10';   // youngest star-certificate band = U7,U8,U9
  if (a <= 12) return 'U11-U12';  // U10-U12 are pillar (not star); see pillarBandForTeam
  if (a <= 14) return 'U14';
  return 'U16-U19';
}

/** True when a team should use the star/certificate experience. */
export function isYoungTeam(id) {
  return YOUNG_BANDS.includes(bandForTeamId(id));
}

/**
 * EVAL_DATA rubric key for the PILLAR (level) report/form.
 * U10 teams carry the age_group text "U11-U12" but get their OWN, simpler
 * descriptors, so they resolve to the 'U10' band. Everyone else uses their
 * age_group as-is. `ageGroup` is the team's age_group text (already the key
 * for U11-U12 / U14 / U16-U19).
 */
export function pillarBandForTeam(teamId, ageGroup) {
  return ageFromTeamId(teamId) === 10 ? 'U10' : ageGroup;
}

// ── The matrices ──────────────────────────────────────────────────────────────
// skill.key MUST be the canonical ratings.skill so a ladder tap saves as a
// normal Technical rating. ladder[lang] is 5 rungs (index 0 = 1 star).
const L = (en, nl) => ({ en, nl });

export const YOUNG_MATRIX = {
  'U7-U8': {
    domain: 'Technical',
    skills: [
      { key: 'Ball carrying', label: L('Dribbling', 'Dribbelen'), ladder: {
        en: ['Moves the ball with the stick', 'Keeps it close and in front', 'Looks up while carrying', 'Carries into space and changes direction', 'Takes the ball past a friend in a game'],
        nl: ['Beweegt de bal met de stick', 'Houdt de bal dichtbij en voor zich', 'Kijkt op tijdens het dribbelen', 'Dribbelt de ruimte in en verandert van richting', 'Gaat in een spel voorbij een medespeler'] } },
      { key: 'Passing', label: L('Passing', 'Passen'), ladder: {
        en: ['Pushes the ball (not a hit)', 'Keeps the pass on the ground', 'Pushes to a friend they can see', 'Looks or calls before passing', 'Passes to a friend in space in a game'],
        nl: ['Duwt de bal (geen slag)', 'Houdt de pass over de grond', 'Duwt naar een medespeler die hij ziet', 'Kijkt of roept voor het passen', 'Past in een spel naar een vrije medespeler'] } },
      { key: 'Receiving', label: L('Receiving', 'Aannemen'), ladder: {
        en: ['Stops the ball with the stick', 'Stops it dead (no bounce away)', 'Stick down ready before it arrives', 'First touch into space', 'Receives and plays on in a game'],
        nl: ['Stopt de bal met de stick', 'Stopt de bal dood (stuitert niet weg)', 'Stick klaar op de grond voor de bal komt', 'Eerste aanname de ruimte in', 'Neemt aan en speelt door in een spel'] } },
      { key: 'Finishing', label: L('Shooting', 'Scoren'), ladder: {
        en: ['Has a go at goal', 'Keeps the shot on the ground', 'Pushes the ball at the goal', 'Aims for a corner', 'Scores in a game'],
        nl: ['Probeert te scoren', 'Houdt het schot over de grond', 'Duwt de bal naar het doel', 'Mikt op een hoek', 'Scoort in een spel'] } },
      { key: 'Elimination (1v1)', label: L('Beating a player', 'Voorbijgaan (1v1)'), ladder: {
        en: ['Runs at the defender', 'Keeps the ball close going past', 'Changes speed or direction', 'Gets past and keeps going', 'Beats a player in a game'],
        nl: ['Gaat op de verdediger af', 'Houdt de bal dichtbij bij het voorbijgaan', 'Verandert van snelheid of richting', 'Gaat voorbij en speelt door', 'Gaat voorbij een speler in een spel'] } },
    ],
    life: [
      { key: 'teamwork', icon: '🤝', label: L('Teamwork', 'Samenspel'),
        default: L('A great little team player.', 'Een echte kleine ploegspeler.'),
        options: [
          L('Loves to share the ball and pass to friends.', 'Deelt graag de bal en past naar vriendjes.'),
          L('Cheers everyone on and lifts the whole team.', 'Moedigt iedereen aan en tilt het hele team op.'),
          L('Always includes friends and plays together.', 'Betrekt altijd vriendjes en speelt samen.') ] },
      { key: 'listening', icon: '👂', label: L('Listening & trying', 'Luisteren & proberen'),
        default: L('Listens well and always tries.', 'Luistert goed en probeert altijd.'),
        options: [
          L('Listens to the coach and gives everything a go.', 'Luistert naar de coach en probeert alles.'),
          L('Never gives up, even when it is tricky.', 'Geeft nooit op, ook als het moeilijk is.'),
          L('Soaks up every tip and tries it straight away.', 'Onthoudt elke tip en probeert het meteen.') ] },
      { key: 'respect', icon: '😄', label: L('Respect & fun', 'Respect & plezier'),
        default: L('Plays with a big smile.', 'Speelt met een grote glimlach.'),
        options: [
          L('Always plays fair and with a smile.', 'Speelt altijd eerlijk en met een glimlach.'),
          L('Kind to teammates and opponents alike.', 'Vriendelijk voor ploeggenoten en tegenstanders.'),
          L('Makes hockey fun for everyone around.', 'Maakt hockey leuk voor iedereen.') ] },
      { key: 'lookingup', icon: '👀', label: L('Looking up', 'Opkijken'),
        default: L('Starting to look up and find space.', 'Begint op te kijken en ruimte te zoeken.'),
        options: [
          L('Keeps their head up to see the game.', 'Houdt het hoofd omhoog om het spel te zien.'),
          L('Finds the free space more and more.', 'Vindt steeds vaker de vrije ruimte.'),
          L('Looks for a friend before playing the ball.', 'Zoekt een vriendje voor hij de bal speelt.') ] },
    ],
  },

  'U9-U10': {
    domain: 'Technical',
    skills: [
      { key: 'Ball carrying', label: L('Dribbling', 'Dribbelen'), ladder: {
        en: ['Keeps the ball close', 'Stops it under control on the cone', 'Looks up while carrying at pace', 'Low, quick turns with the ball', 'Beats an opponent and drives into space'],
        nl: ['Houdt de bal dichtbij', 'Stopt onder controle op de pion', 'Kijkt op tijdens het dribbelen op tempo', 'Lage, snelle draaien met de bal', 'Gaat voorbij een tegenstander de ruimte in'] } },
      { key: 'Passing', label: L('Passing', 'Passen'), ladder: {
        en: ['Push pass on the ground', 'Stick down early to give the pass', 'Calls the name and passes', 'Passes through a gap', 'Picks the free player under a little pressure'],
        nl: ['Pushpass over de grond', 'Stick vroeg klaar om te passen', 'Roept de naam en past', 'Past door een gaatje', 'Kiest de vrije speler onder wat druk'] } },
      { key: 'Receiving', label: L('Receiving', 'Aannemen'), ladder: {
        en: ['Stops the ball with the stick down', 'Stick ready before the ball arrives', 'Looks up before it arrives', 'First touch into space', 'Receives and turns into space in a game'],
        nl: ['Stopt de bal met de stick op de grond', 'Stick klaar voor de bal komt', 'Kijkt op voor de bal komt', 'Eerste aanname de ruimte in', 'Neemt aan en draait de ruimte in een spel'] } },
      { key: 'Finishing', label: L('Shooting', 'Scoren'), ladder: {
        en: ['Gets a shot away', 'Keeps it low', 'Looks at goal before shooting', 'Aims for a corner in the circle', 'Scores from open play in a game'],
        nl: ['Krijgt een schot weg', 'Houdt het laag', 'Kijkt naar het doel voor het schot', 'Mikt op een hoek in de cirkel', 'Scoort uit open spel in een wedstrijd'] } },
      { key: 'Elimination (1v1)', label: L('Beating a player', 'Voorbijgaan (1v1)'), ladder: {
        en: ['Runs at the defender with control', 'Slows then goes (change of pace)', 'Shows the defender one way', 'Beats them and drives forward', 'Beats a player and creates a chance'],
        nl: ['Gaat gecontroleerd op de verdediger af', 'Vertraagt en versnelt (tempowissel)', 'Toont de verdediger een kant', 'Gaat voorbij en speelt vooruit', 'Gaat voorbij en creeert een kans'] } },
      { key: 'Defensive skills', label: L('Defending', 'Verdedigen'), ladder: {
        en: ['Chases and tries to win the ball', 'Gets low with the stick down', 'Stays balanced, does not dive in', 'Shows the attacker one way', 'Wins the ball and plays forward'],
        nl: ['Jaagt en probeert de bal te winnen', 'Zakt door de knieen met de stick op de grond', 'Blijft in balans, duikt er niet in', 'Stuurt de aanvaller een kant op', 'Wint de bal en speelt vooruit'] } },
    ],
    life: [
      { key: 'teamwork', icon: '🤝', label: L('Teamwork', 'Samenspel'),
        default: L('A real team player.', 'Een echte ploegspeler.'),
        options: [
          L('Plays for the team and makes others better.', 'Speelt voor het team en maakt anderen beter.'),
          L('Always available and shares the ball.', 'Altijd aanspeelbaar en deelt de bal.'),
          L('Puts the team first, every time.', 'Zet het team altijd op de eerste plaats.') ] },
      { key: 'communication', icon: '🗣️', label: L('Communication', 'Communicatie'),
        default: L('Talks and calls on the pitch.', 'Praat en roept op het veld.'),
        options: [
          L('Calls for the ball and guides teammates.', 'Vraagt om de bal en stuurt ploeggenoten.'),
          L('A real voice on the pitch.', 'Een echte stem op het veld.'),
          L('Communicates before and after the pass.', 'Communiceert voor en na de pass.') ] },
      { key: 'gamesense', icon: '🧠', label: L('Game sense', 'Spelinzicht'),
        default: L('Finds space and makes simple choices.', 'Zoekt ruimte en maakt simpele keuzes.'),
        options: [
          L('Reads the game and picks the right option.', 'Leest het spel en kiest de juiste optie.'),
          L('Always looking for space to receive.', 'Zoekt altijd ruimte om aan te nemen.'),
          L('Understands when to pass and when to carry.', 'Weet wanneer te passen en wanneer te dribbelen.') ] },
      { key: 'attitude', icon: '💪', label: L('Attitude', 'Instelling'),
        default: L('Keeps going and bounces back.', 'Blijft gaan en herstelt snel.'),
        options: [
          L('Works hard from start to finish.', 'Werkt hard van begin tot eind.'),
          L('Bounces back quickly after a mistake.', 'Herstelt snel na een foutje.'),
          L('Brings energy and focus every session.', 'Brengt elke training energie en focus.') ] },
      { key: 'respect', icon: '🤝', label: L('Respect', 'Respect'),
        default: L('Respectful to everyone.', 'Respectvol voor iedereen.'),
        options: [
          L('Respects teammates, opponents and the ref.', 'Respecteert medespelers, tegenstanders en de scheids.'),
          L('Always fair, wins and loses well.', 'Altijd eerlijk, wint en verliest sportief.'),
          L('A model of good sportsmanship.', 'Een voorbeeld van sportiviteit.') ] },
    ],
  },
};

// ── Accessors ─────────────────────────────────────────────────────────────────
export function matrixForTeamId(id) {
  const band = bandForTeamId(id);
  return YOUNG_MATRIX[band] || null;
}

/** Star skills for a band (or null). */
export function starSkills(band) { return YOUNG_MATRIX[band]?.skills || []; }

/** Hockey-for-life values for a band. */
export function lifeValues(band) { return YOUNG_MATRIX[band]?.life || []; }

/**
 * Resolve the certificate line for one life value given the coach's pick.
 * `choice` is an index into options[], or null/undefined -> the default line.
 * Always returns a positive line, so a skipped value still reads well.
 */
export function lifeLine(value, choice, lang = 'en') {
  const opt = (choice != null && value.options[choice]) ? value.options[choice] : value.default;
  return opt[lang] || opt.en;
}

/**
 * Build the display model for a certificate.
 *   band          'U7-U8' | 'U9-U10'
 *   levelBySkill  { '<canonical skill>': level 1-5 } (from the player's ratings)
 *   lifePicks     { '<value key>': optionIndex }     (coach picks; absent = default)
 * Returns { skills:[{label, stars}], life:[{icon,label,line}] } or null.
 */
export function certificateModel(band, levelBySkill = {}, lifePicks = {}, lang = 'en') {
  const m = YOUNG_MATRIX[band];
  if (!m) return null;
  // Half stars: when more than one person rates a skill (the coach and an
  // evaluator, say) we show the AVERAGE, snapped to the nearest half. A 2 and
  // a 3 become 2.5 rather than being rounded away to 3.
  const clamp = n => Math.max(0, Math.min(5, Math.round((n || 0) * 2) / 2));
  return {
    skills: m.skills.map(sk => ({ label: sk.label[lang] || sk.label.en, stars: clamp(levelBySkill[sk.key]) })),
    life:   m.life.map(v => ({ icon: v.icon, label: v.label[lang] || v.label.en, line: lifeLine(v, lifePicks[v.key], lang) })),
  };
}
